import type { QueryClient } from '@tanstack/react-query'
import type { IOrdinalMarketSummary, IOrdinalOrder, IOrdinalsMediaStreamItem } from 'api/api'

/**
 * One ordinal's live state, as published by `Ordinals::StateEvents`
 * (docs/ordinal-state-events.md).
 *
 * The same envelope arrives on up to three lanes with different fields filled
 * in: the public stream omits `market`, and only the per-user EventBus lane
 * carries `viewer`. Patching is therefore **field-wise** — a frame never blanks
 * something it does not carry — and the revision guard is `>=` rather than `>`
 * so a client subscribed to both shared streams applies both frames for the
 * same revision instead of dropping the second one.
 */
export interface OrdinalStateEvent {
    ordinal_id: string
    origin?: string | null
    revision: number
    reason: string
    t?: number
    owner?: OrdinalStateOwner | null
    chain_state?: string | null
    sale?: {
        order_id?: string
        price_sats?: number
        seller_id?: string | null
        buyer_id?: string | null
        txid?: string | null
        settled_at?: string | null
    } | null
    market?: IOrdinalMarketSummary | null
    viewer?: {
        role?: 'buyer' | 'seller' | 'owner' | 'bidder'
        ownership_status?: 'confirmed' | 'unconfirmed'
        bid?: IOrdinalOrder | null
        settlement?: { status?: string; order_id?: string } | null
    } | null
    /** Present on frames the client itself synthesized; never sent by the server. */
    local?: boolean
}

export interface OrdinalStateOwner {
    id: string
    name?: string | null
    username?: string | null
    handle?: string | null
    [key: string]: unknown
}

type StreamPage = {
    items?: IOrdinalsMediaStreamItem[]
    [key: string]: unknown
}

type StreamData = { pages?: StreamPage[]; pageParams?: unknown[] }

export const ORDINAL_STREAM_QUERY_KEY = 'ordinalsMediaStream'
export const ORDINAL_MARKET_QUERY_KEY = 'ordinalMarket'

/** The filter a stream query was built from, recovered from its serialized key. */
export function parseStreamFilter(queryKey: readonly unknown[]): Record<string, unknown> | null {
    if (queryKey?.[0] !== ORDINAL_STREAM_QUERY_KEY) return null
    const raw = queryKey[1]
    if (typeof raw !== 'string') return null
    try {
        const parsed = JSON.parse(raw)
        return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
    } catch {
        return null
    }
}

export function isMineStreamFilter(filter: Record<string, unknown> | null): boolean {
    return !!filter && !!filter.ordinals && filter.ordinals_scope === 'mine'
}

export function isMarketStreamFilter(filter: Record<string, unknown> | null): boolean {
    return !!filter && !!filter.ordinals && filter.market === true
}

/**
 * A tile whose lens no longer describes it — a sold listing on the Market tab,
 * an ordinal the viewer just sold out of My Ordinals.
 *
 * It is marked rather than removed. Ranked walls only change composition on an
 * explicit refresh, and yanking a row out from under someone reading it is the
 * behavior this whole change exists to stop. The marker renders a chip and
 * feeds the updates pill, which is the refresh that finally prunes it.
 */
function nextLensStale(
    item: IOrdinalsMediaStreamItem,
    event: OrdinalStateEvent,
    filter: Record<string, unknown> | null,
    currentUserId: string | null,
    nextMarket: IOrdinalMarketSummary | null | undefined,
): { lens_stale?: boolean; lens_stale_reason?: 'sold' | 'unlisted' } | null {
    if (isMarketStreamFilter(filter)) {
        // A public sale is authoritative even if its market lane was missed.
        // Stale public frames have their owner stripped before reaching here.
        if (!event.market) {
            // A delayed sale publication can already carry the buyer's new
            // listing. Its equally fresh market lane may have arrived first.
            if (
                (nextMarket?.ask || nextMarket?.settlement_pending) &&
                Number(nextMarket.state_revision ?? 0) >= event.revision
            )
                return null
            return event.reason === 'sold' && event.owner !== undefined
                ? { lens_stale: true, lens_stale_reason: 'sold' }
                : null
        }
        if (nextMarket?.ask || nextMarket?.settlement_pending) {
            return item.lens_stale ? { lens_stale: false, lens_stale_reason: undefined } : null
        }
        return {
            lens_stale: true,
            lens_stale_reason:
                event.reason === 'sold' ||
                (event.reason === 'snapshot' &&
                    !!event.sale?.buyer_id &&
                    event.sale.buyer_id === event.owner?.id &&
                    (item.owner_id !== event.owner.id ||
                        (!!event.sale.seller_id &&
                            item.market?.ask?.seller_id === event.sale.seller_id))) ||
                (event.reason !== 'unlisted' && item.lens_stale_reason === 'sold')
                    ? 'sold'
                    : 'unlisted',
        }
    }

    if (isMineStreamFilter(filter) && event.owner !== undefined && currentUserId) {
        const ownerId = event.owner?.id ? String(event.owner.id) : null
        return ownerId !== String(currentUserId)
            ? { lens_stale: true, lens_stale_reason: 'sold' }
            : { lens_stale: false, lens_stale_reason: undefined }
    }

    return null
}

function patchItem(
    item: IOrdinalsMediaStreamItem,
    event: OrdinalStateEvent,
    filter: Record<string, unknown> | null,
    opts: { currentUserId: string | null; marketEnabled: boolean },
): IOrdinalsMediaStreamItem {
    const known = Number(item.state_revision ?? 0)
    const publicApplies = event.revision >= known
    const ownershipApplies =
        event.viewer?.ownership_status &&
        isMineStreamFilter(filter) &&
        event.revision >= Number(item.ownership_state_revision ?? known)
    const nextMarket = opts.marketEnabled
        ? patchMarket(item.market, event, opts.currentUserId)
        : item.market
    if (!publicApplies && !ownershipApplies && nextMarket === item.market) return item

    const next: IOrdinalsMediaStreamItem = {
        ...item,
        ownership_state_revision: item.ownership_state_revision ?? known,
    }
    if (publicApplies) {
        next.state_revision = event.revision
        if (event.owner !== undefined) {
            next.owner_id = event.owner?.id ? String(event.owner.id) : null
            if (isMineStreamFilter(filter) && next.owner_id !== opts.currentUserId) {
                next.ownership_status = undefined
                next.ownership_state_revision = event.revision
            }
            next.parent = {
                ...(item.parent || {}),
                user: event.owner ? { ...event.owner, id: String(event.owner.id) } : null,
            }
        }
        if (event.chain_state !== undefined) next.chain_state = event.chain_state
    }
    if (nextMarket !== item.market) next.market = nextMarket
    if (ownershipApplies) {
        next.ownership_status = event.viewer!.ownership_status
        next.ownership_state_revision = event.revision
    }

    // Public, market and private messages may cross in transit. A newer public
    // frame must not discard an older ownership confirmation or book update.
    const lensEvent = {
        ...event,
        owner: publicApplies ? event.owner : undefined,
        market: nextMarket !== item.market ? event.market : undefined,
    }
    const lens = nextLensStale(item, lensEvent, filter, opts.currentUserId, nextMarket)
    if (lens) Object.assign(next, lens)

    return next
}

/**
 * Patch every mounted ordinals stream in place.
 *
 * Object identity is preserved for everything the event does not touch, so
 * `React.memo` on the tile renderer re-renders exactly one card rather than the
 * whole wall.
 */
export function applyOrdinalStateToStreams(
    queryClient: QueryClient,
    event: OrdinalStateEvent,
    opts: { currentUserId: string | null; marketEnabled: boolean },
): number {
    let patched = 0

    // Walked rather than `setQueriesData`, which does not hand the updater its
    // key — and the key is what says whether a query is the Market lens, the
    // viewer's own list, or a public wall.
    queryClient
        .getQueryCache()
        .findAll({ queryKey: [ORDINAL_STREAM_QUERY_KEY] })
        .forEach(query => {
            const filter = parseStreamFilter(query.queryKey)
            const data = query.state.data as StreamData | undefined
            if (!data?.pages?.length) return

            let changed = false
            const pages = data.pages.map(page => {
                const items = page?.items
                if (!Array.isArray(items) || !items.length) return page

                let pageChanged = false
                const nextItems = items.map(item => {
                    if (String(item?.id || '') !== String(event.ordinal_id)) return item
                    const nextItem = patchItem(item, event, filter, opts)
                    if (nextItem !== item) pageChanged = true
                    return nextItem
                })

                if (!pageChanged) return page
                changed = true
                return { ...page, items: nextItems }
            })

            if (!changed) return
            patched += 1
            queryClient.setQueryData(query.queryKey, { ...data, pages })
        })

    return patched
}

/**
 * The rail's own query, read by stream tiles, thread heroes, and sale receipts
 * alike — so patching it here flips all three at once.
 */
function patchMarket(
    previous: IOrdinalMarketSummary | null | undefined,
    event: OrdinalStateEvent,
    currentUserId: string | null,
): IOrdinalMarketSummary | null | undefined {
    const bookApplies = event.market && event.revision >= Number(previous?.state_revision ?? 0)
    const bidApplies =
        event.viewer?.bid !== undefined &&
        event.revision >=
            Number(previous?.viewer?.bid_state_revision ?? previous?.state_revision ?? 0)
    if (!bookApplies && !bidApplies) return previous
    return {
        ...(previous || {}),
        ...(bookApplies ? { ...event.market, state_revision: event.revision } : {}),
        viewer: {
            ...(previous?.viewer || {}),
            bid_state_revision:
                previous?.viewer?.bid_state_revision ?? Number(previous?.state_revision ?? 0),
            ...(bookApplies && event.owner !== undefined
                ? { is_owner: !!(currentUserId && event.owner?.id === currentUserId) }
                : {}),
            ...(bidApplies ? { bid: event.viewer!.bid, bid_state_revision: event.revision } : {}),
        },
    }
}

export function applyOrdinalStateToMarket(
    queryClient: QueryClient,
    event: OrdinalStateEvent,
    opts: { currentUserId: string | null },
): boolean {
    let applied = false
    queryClient
        .getQueryCache()
        .findAll({ queryKey: [ORDINAL_MARKET_QUERY_KEY, event.ordinal_id] })
        .forEach(query => {
            const previous = query.state.data as IOrdinalMarketSummary | null | undefined
            const next = patchMarket(previous, event, opts.currentUserId)
            if (next === previous) return
            queryClient.setQueryData(query.queryKey, next)
            applied = true
        })
    return applied
}

/** True when this event says the signed-in viewer just became the owner. */
export function isViewerPurchase(event: OrdinalStateEvent): boolean {
    return (
        (event.reason === 'sold' || event.reason === 'snapshot') && event.viewer?.role === 'buyer'
    )
}

/** Stream query keys for the viewer's own ordinals, whether or not they hold data. */
export function mineStreamQueryKeys(queryClient: QueryClient): (readonly unknown[])[] {
    return queryClient
        .getQueryCache()
        .findAll({ queryKey: [ORDINAL_STREAM_QUERY_KEY] })
        .filter(query => isMineStreamFilter(parseStreamFilter(query.queryKey)))
        .map(query => query.queryKey as readonly unknown[])
}

export function mineStreamsHaveOrdinal(queryClient: QueryClient, ordinalId: string): boolean {
    const keys = mineStreamQueryKeys(queryClient)
    return (
        keys.length > 0 &&
        keys.every(key => {
            const data = queryClient.getQueryData<StreamData>(key)
            return !!data?.pages?.some(page =>
                page?.items?.some(item => String(item?.id || '') === String(ordinalId)),
            )
        })
    )
}

/**
 * Put a freshly purchased ordinal at the head of the viewer's own list. The
 * caller supplies the hydrated row (one targeted `ids[]` fetch); this only
 * decides placement and guards against a double insert.
 */
export function prependOrdinalToMineStreams(
    queryClient: QueryClient,
    item: IOrdinalsMediaStreamItem,
): number {
    let inserted = 0

    mineStreamQueryKeys(queryClient).forEach(queryKey => {
        const data = queryClient.getQueryData<StreamData>(queryKey)
        if (!data?.pages?.length) return
        const alreadyPresent = data.pages.some(page =>
            page?.items?.some(existing => String(existing?.id || '') === String(item.id)),
        )
        if (alreadyPresent) return

        const [first, ...rest] = data.pages
        queryClient.setQueryData(queryKey, {
            ...data,
            pages: [{ ...first, items: [item, ...(first?.items || [])] }, ...rest],
        })
        inserted += 1
    })

    return inserted
}

/** Preserve socket patches when a page fetch finishes with an older row snapshot. */
export function preserveOrdinalStreamState(previous: unknown, incoming: unknown): unknown {
    const oldData = previous as StreamData | undefined
    const nextData = incoming as StreamData | undefined
    if (!oldData?.pages || !nextData?.pages) return incoming
    const rows = new Map(
        oldData.pages.flatMap(page => (page.items || []).map(row => [row.id, row] as const)),
    )
    const pages = nextData.pages.map(page => ({
        ...page,
        items: page.items?.map(row => {
            const old = rows.get(row.id)
            return old && Number(old.state_revision ?? 0) > Number(row.state_revision ?? 0)
                ? old
                : row
        }),
    }))
    // Fresh HTTP rows must keep their identity so tile effects can reseed the
    // separate market cache, even when the stream snapshot itself is unchanged.
    return { ...nextData, pages }
}
